import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ArrowLeft, RefreshCw, Sparkles, ExternalLink, Loader2, X, Search, Wand2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  useWebTrends,
  useFetchWebTrends,
  useUpdateWebTrendStatus,
  useGenerateTrendArticle,
  type WebTrend,
} from '@/hooks/useWebTrends';
import { useNewsCategories } from '@/hooks/useNewsCategories';
import AnalyzeTrendsDialog from '@/components/admin/trends/AnalyzeTrendsDialog';

export default function WebTrendsPage() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<'nueva' | 'usada' | 'descartada' | 'all'>('nueva');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [categoryId, setCategoryId] = useState<string>('');
  const [analyzeOpen, setAnalyzeOpen] = useState(false);


  const { data: trends = [], isLoading } = useWebTrends(statusFilter);
  const { data: categories = [] } = useNewsCategories();
  const fetchMut = useFetchWebTrends();
  const statusMut = useUpdateWebTrendStatus();
  const generateMut = useGenerateTrendArticle();

  const filtered = trends.filter((t) =>
    !search || t.title.toLowerCase().includes(search.toLowerCase()) ||
    (t.source_name ?? '').toLowerCase().includes(search.toLowerCase())
  );

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleRefresh = async () => {
    try {
      const res = await fetchMut.mutateAsync();
      toast.success(`Actualizado: ${res.new_count} nuevas (RSS ${res.rss_count} · Web ${res.web_count})`);
    } catch (e: any) {
      toast.error(e?.message ?? 'Error al actualizar');
    }
  };

  const handleGenerate = async (withCover = false) => {
    if (selected.size === 0) return;
    try {
      const res = await generateMut.mutateAsync({
        trend_ids: Array.from(selected),
        category_id: categoryId || null,
        include_cover_image: withCover,
      });
      toast.success('Borrador generado. Abriendo editor…');
      setSelected(new Set());
      navigate(`/trends-management?edit=${res.article_id}`);
    } catch (e: any) {
      toast.error(e?.message ?? 'Error al generar artículo');
    }
  };

  const handleDiscard = (id: string) => {
    statusMut.mutate({ id, status: 'descartada' });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/trends-management')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Tendencias de la Web</h1>
            <p className="text-muted-foreground mt-1">
              Noticias importadas desde portales RSS y búsqueda IA. Selecciona varias para generar un artículo LIDER IA.
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setAnalyzeOpen(true)}>
            <Wand2 className="mr-2 h-4 w-4" />
            Analizar con IA
          </Button>
          <Button onClick={handleRefresh} disabled={fetchMut.isPending}>
            {fetchMut.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
            Actualizar
          </Button>
        </div>
      </div>

      <AnalyzeTrendsDialog open={analyzeOpen} onOpenChange={setAnalyzeOpen} />


      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="flex flex-wrap gap-3 items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar por título o fuente"
                className="pl-8"
              />
            </div>
            <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as any)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="nueva">Nuevas</SelectItem>
                <SelectItem value="usada">Usadas</SelectItem>
                <SelectItem value="descartada">Descartadas</SelectItem>
                <SelectItem value="all">Todas</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {selected.size > 0 && (
            <div className="flex flex-wrap items-center gap-3 p-3 rounded-lg bg-primary/5 border border-primary/20">
              <span className="text-sm font-medium">{selected.size} seleccionada(s)</span>
              <Select value={categoryId} onValueChange={setCategoryId}>
                <SelectTrigger className="w-[220px]">
                  <SelectValue placeholder="Categoría (opcional)" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={() => handleGenerate(false)} disabled={generateMut.isPending}>
                {generateMut.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
                Generar artículo LIDER IA
              </Button>
              <Button variant="outline" onClick={() => handleGenerate(true)} disabled={generateMut.isPending}>
                {generateMut.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
                Generar con portada
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>Limpiar</Button>
            </div>
          )}

          {isLoading ? (
            <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10"></TableHead>
                  <TableHead>Título</TableHead>
                  <TableHead className="w-[140px]">Fuente</TableHead>
                  <TableHead className="w-[110px]">Tipo</TableHead>
                  <TableHead className="w-[120px]">Fecha</TableHead>
                  <TableHead className="w-[100px]">Estado</TableHead>
                  <TableHead className="w-[120px] text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">Sin tendencias. Pulsa Actualizar.</TableCell></TableRow>
                ) : filtered.map((t: WebTrend) => (
                  <TableRow key={t.id}>
                    <TableCell>
                      <Checkbox
                        checked={selected.has(t.id)}
                        onCheckedChange={() => toggle(t.id)}
                        disabled={t.status !== 'nueva'}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="font-medium line-clamp-2">{t.title}</div>
                      {t.summary && <div className="text-xs text-muted-foreground line-clamp-2 mt-1">{t.summary}</div>}
                    </TableCell>
                    <TableCell className="text-sm">{t.source_name ?? '—'}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">{t.source_type === 'rss' ? 'RSS' : 'Web IA'}</Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {t.published_at ? new Date(t.published_at).toLocaleDateString() : '—'}
                    </TableCell>
                    <TableCell>
                      <Badge variant={t.status === 'usada' ? 'default' : t.status === 'descartada' ? 'secondary' : 'outline'}>
                        {t.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button asChild variant="ghost" size="icon" title="Abrir">
                          <a href={t.url} target="_blank" rel="noreferrer"><ExternalLink className="h-4 w-4" /></a>
                        </Button>
                        {t.status === 'nueva' && (
                          <Button variant="ghost" size="icon" title="Descartar" onClick={() => handleDiscard(t.id)}>
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
